import type React from "react";
import type { ContentsProps } from "../../../types";

const Targets: React.FC<ContentsProps> = ({ data, isLoading }) => {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-80 bg-white/50 rounded-2xl backdrop-blur-sm">
        <div className="relative">
          <div className="h-16 w-16 rounded-full border-4 border-slate-100 border-t-slate-800 animate-spin"></div>
          <div className="mt-4 text-sm text-slate-600 text-center">
            Loading data...
          </div>
        </div>
      </div>
    );
  }

  if (!data || !data.total_targets || !data.total_targets.total) {
    return (
      <div className="h-80 bg-white rounded-2xl shadow-sm border border-slate-100 flex items-center justify-center p-6">
        <div className="text-center space-y-2">
          <p className="text-slate-600 font-medium">No target data available</p>
          <p className="text-sm text-slate-400">Please check back later</p>
        </div>
      </div>
    );
  }

  const { total_target, targets } = data.total_targets.total;
  const { A, B, C } = targets;

  // Fix for NaN percentages when total_target is 0
  const percentA =
    total_target === 0 ? 0 : Math.round((A / total_target) * 100);
  const percentB =
    total_target === 0 ? 0 : Math.round((B / total_target) * 100);
  const percentC =
    total_target === 0 ? 0 : Math.round((C / total_target) * 100);

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 transition-all duration-200 hover:shadow-md">
      <div className="flex flex-col space-y-8">
        {/* Total Targets Header */}
        <div className="text-center pb-6 border-b border-slate-100">
          <h2 className="text-slate-500 text-sm font-medium uppercase tracking-wider mb-3">
            Total Targets
          </h2>
          <div className="inline-flex items-center justify-center">
            <span className="text-5xl font-bold bg-gradient-to-r from-indigo-600 to-violet-600 bg-clip-text text-transparent">
              {total_target.toLocaleString()}
            </span>
          </div>
        </div>

        {/* Categories Breakdown */}
        <div className="space-y-6">
          <h3 className="text-slate-500 text-sm font-medium uppercase tracking-wider">
            Categories Breakdown
          </h3>

          {/* Category A */}
          <div className="space-y-2 group">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-slate-700">
                Category A
              </span>
              <span className="text-sm font-semibold text-slate-900">
                {A.toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-indigo-500 h-full rounded-full transition-all duration-500 ease-out group-hover:opacity-80"
                style={{ width: `${percentA}%` }}
              ></div>
            </div>
          </div>

          {/* Category B */}
          <div className="space-y-2 group">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-slate-700">
                Category B
              </span>
              <span className="text-sm font-semibold text-slate-900">
                {B.toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-violet-500 h-full rounded-full transition-all duration-500 ease-out group-hover:opacity-80"
                style={{ width: `${percentB}%` }}
              ></div>
            </div>
          </div>

          {/* Category C */}
          <div className="space-y-2 group">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium text-slate-700">
                Category C
              </span>
              <span className="text-sm font-semibold text-slate-900">
                {C.toLocaleString()}
              </span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2.5 overflow-hidden">
              <div
                className="bg-fuchsia-500 h-full rounded-full transition-all duration-500 ease-out group-hover:opacity-80"
                style={{ width: `${percentC}%` }}
              ></div>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-4">
          <div className="group relative overflow-hidden bg-gradient-to-br from-indigo-50 to-white p-6 rounded-xl border border-indigo-100/50 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
            <div className="relative z-10">
              <p className="text-indigo-600 font-bold text-2xl mb-1">
                {A.toLocaleString()}
              </p>
              <p className="text-slate-600 font-medium">Category A</p>
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-indigo-400/0 to-indigo-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>

          <div className="group relative overflow-hidden bg-gradient-to-br from-violet-50 to-white p-6 rounded-xl border border-violet-100/50 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
            <div className="relative z-10">
              <p className="text-violet-600 font-bold text-2xl mb-1">
                {B.toLocaleString()}
              </p>
              <p className="text-slate-600 font-medium">Category B</p>
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-violet-400/0 to-violet-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>

          <div className="group relative overflow-hidden bg-gradient-to-br from-fuchsia-50 to-white p-6 rounded-xl border border-fuchsia-100/50 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
            <div className="relative z-10">
              <p className="text-fuchsia-600 font-bold text-2xl mb-1">
                {C.toLocaleString()}
              </p>
              <p className="text-slate-600 font-medium">Category C</p>
            </div>
            <div className="absolute inset-0 bg-gradient-to-r from-fuchsia-400/0 to-fuchsia-400/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Targets;
